import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const API = config.apiUrl;

const IdentificationPage = ({ setCurrentUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Log de diagnostic au chargement
  React.useEffect(() => {
    console.log('🔍 Configuration API:');
    console.log('  API endpoint:', API);
    console.log('  URL complète users:', `${API}/users`);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/users`, formData, {
        timeout: 30000, // 30 secondes
        headers: {
          'Content-Type': 'application/json'
        }
      });
      console.log('✅ User created successfully:', response.data);
      setCurrentUser(response.data);
      navigate('/questionnaire');
    } catch (err) {
      console.error('❌ Error creating user:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        url: `${API}/users`
      });
      
      // Message d'erreur plus détaillé
      let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Le serveur met trop de temps à répondre. Veuillez réessayer.';
      } else if (err.response) {
        // Le serveur a répondu avec un code d'erreur
        if (err.response.status === 500) {
          errorMessage = 'Erreur du serveur. Veuillez réessayer dans quelques instants.';
        } else if (err.response.status === 404) {
          errorMessage = 'Service non disponible. Veuillez contacter le support.';
        } else {
          errorMessage = `Erreur ${err.response.status}: ${err.response.data?.detail || 'Veuillez réessayer.'}`;
        }
      } else if (err.request) {
        // La requête a été envoyée mais pas de réponse
        errorMessage = 'Impossible de joindre le serveur. Vérifiez votre connexion internet.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" data-testid="identification-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Stress Management Test</h1>
            <p className="text-slate-600">Questionnaire d'évaluation des mécanismes d'adaptation</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Prénom et Nom sur la même ligne */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Prénom
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Votre prénom"
                  data-testid="first-name-input"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-semibold text-slate-700 mb-2">
                  Nom
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  placeholder="Votre nom"
                  data-testid="last-name-input"
                />
              </div>
            </div>

            {/* Email sur une ligne séparée */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                placeholder="votre.email@exemple.com"
                data-testid="email-input"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="error-message">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-identification-btn"
            >
              {loading ? 'Chargement...' : 'Commencer le questionnaire'}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-xs text-slate-500 text-center mb-3">
              Ce questionnaire évalue vos stratégies d'adaptation face au stress.
              La durée estimée est de 5-10 minutes.
            </p>
            <div className="text-center">
              <button
                onClick={() => window.location.href = '/explications'}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline transition"
                data-testid="explications-link"
              >
                📖 En savoir plus sur les mécanismes d'adaptation
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdentificationPage;
